import React, { useState, useEffect } from 'react';
import { useAuth } from '../pages/AuthContext';
import { motion } from 'framer-motion';
import { T, getStatusClass } from './theme';
import API_BASE_URL from '../config/api';
const fadeUp = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } };

const YoutubePage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const { role } = useAuth();

  const isAdmin = (role || '').split(',').map(s => s.trim().toUpperCase()).includes('ADMIN');

  const getUserName = (email) => {
    const user = users.find(u => u.emailID === email);
    return user ? `${user.name || ''} ${user.surname || ''}`.trim() || email : email || '';
  };

  const getVideoId = (url) => {
    try {
      if (url.includes('youtu.be/')) return url.split('youtu.be/')[1].split('?')[0];
      if (url.includes('watch?v=')) return url.split('watch?v=')[1].split('&')[0];
      return '';
    } catch { return ''; }
  };

  const getThumbnailUrl = (url) => { const id = getVideoId(url); return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : ''; };
  const getVideoUrl = (url) => { const id = getVideoId(url); return id ? `https://www.youtube.com/watch?v=${id}` : url; };

  useEffect(() => {
    const fetchVideos = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/vrv-youtubecontent-getAll`);
        const data = await res.json();
        const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        const all = (parsed.data || []).filter(v => {
          if (v.status && v.status !== 'APPROVED') return false;
          return isAdmin || v.visibility === 'PUBLIC';
        });
        setVideos(all);
        const cats = {};
        all.forEach(v => { cats[v.category || 'Uncategorized'] = true; });
        setExpandedCategories(cats);
        const token = localStorage.getItem('vrvToken');
        if (token) {
          const ur = await fetch(`${API_BASE_URL}/vrv-users-getAll`, { headers: { Authorization: `Bearer ${token}` } });
          const ud = await ur.json();
          const up = typeof ud.body === 'string' ? JSON.parse(ud.body) : ud;
          setUsers(up.users || []);
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchVideos();
  }, [isAdmin]);

  const toggleCategory = (cat) => setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));

  const groupedVideos = videos.reduce((acc, v) => {
    const cat = v.category || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(v);
    return acc;
  }, {});
  Object.values(groupedVideos).forEach(arr => arr.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
  const sortedCategories = Object.keys(groupedVideos).sort((a, b) => a.localeCompare(b));

  const filtered = search
    ? videos.filter(v => [v.title, v.category, v.author, getUserName(v.createdBy)].some(val => String(val || '').toLowerCase().includes(search.toLowerCase())))
    : null;

  const kpis = [
    { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.723v6.554a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" /></svg>, value: videos.length, label: 'Total Videos' },
    { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>, value: new Set(videos.map(v => v.createdBy).filter(Boolean)).size, label: 'Contributors' },
  ];

  return (
    <div style={{ background: T.bg, minHeight: '100vh', color: T.ink }} className="">

      {/* Hero */}
      <section className="vrv-hero">
        <div className="vrv-container">
          <div className="relative z-10">
            <p className="vrv-tag mb-3">VRV Community</p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>Videos</h1>
            <p className="text-base mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>Watch spiritual content from our community.</p>
            <div className="grid grid-cols-2 gap-3 max-w-lg">
              {kpis.map((k, i) => (
                <div key={i} className="vrv-kpi-card">
                  <div className="mb-1.5" style={{ color: 'rgba(255,255,255,0.8)' }}>{k.icon}</div>
                  <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>{k.value}</p>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.5)' }}>{k.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="vrv-container py-10">

        {/* Search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <p className="text-sm" style={{ color: T.muted }}>{videos.length} video{videos.length !== 1 ? 's' : ''}</p>
          <div className="relative w-full sm:w-72">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: T.muted }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" placeholder="Search videos…" value={search} onChange={e => setSearch(e.target.value)} className="vrv-input pl-10" />
          </div>
        </div>

        {/* Loading shimmer */}
        {loading && (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="vrv-card overflow-hidden">
                <div className="shimmer h-14 w-full" />
                <div className="p-5 space-y-3">
                  {[1, 2].map(j => (
                    <div key={j} className="flex gap-4">
                      <div className="shimmer rounded-xl flex-shrink-0" style={{ width: 144, height: 81 }} />
                      <div className="flex-1 space-y-2 py-1">
                        <div className="shimmer h-4 w-3/4 rounded-lg" />
                        <div className="shimmer h-3 w-1/2 rounded-lg" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Search results */}
        {!loading && search && (
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={{ show: { transition: { staggerChildren: 0.06 } } }} className="space-y-4">
            {filtered.length === 0
              ? <div className="vrv-card text-center py-16"><p className="text-4xl mb-3">🔍</p><p className="font-bold" style={{ color: T.muted }}>No results for "{search}"</p></div>
              : filtered.map((video, i) => <VideoRow key={video.content_id || i} video={video} isAdmin={isAdmin} getUserName={getUserName} getVideoUrl={getVideoUrl} getThumbnailUrl={getThumbnailUrl} />)
            }
          </motion.div>
        )}

        {/* Grouped by category */}
        {!loading && !search && sortedCategories.length > 0 && (
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={{ show: { transition: { staggerChildren: 0.08 } } }} className="space-y-4">
            {sortedCategories.map(category => (
              <motion.div key={category} variants={fadeUp} className="vrv-card overflow-hidden" style={{ transition: 'none' }}>
                {/* Category header */}
                <button onClick={() => toggleCategory(category)}
                  className="w-full flex items-center justify-between px-6 py-4 transition-colors"
                  style={{ background: 'white', borderBottom: expandedCategories[category] ? `1px solid ${T.border}` : 'none' }}
                  onMouseEnter={e => e.currentTarget.style.background = T.stripe}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                  <div className="flex items-center gap-3">
                    <h2 className="text-sm font-bold uppercase tracking-widest" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>{category}</h2>
                    <span className="badge badge-gold">{groupedVideos[category].length}</span>
                  </div>
                  <svg className={`w-4 h-4 transition-transform duration-200 ${expandedCategories[category] ? 'rotate-180' : ''}`}
                    style={{ color: T.muted }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Video rows */}
                {expandedCategories[category] && groupedVideos[category].map((video, i) => (
                  <div key={video.content_id || i} style={{ borderBottom: i < groupedVideos[category].length - 1 ? `1px solid ${T.border}` : 'none' }}>
                    <VideoRow video={video} isAdmin={isAdmin} getUserName={getUserName} getVideoUrl={getVideoUrl} getThumbnailUrl={getThumbnailUrl} />
                  </div>
                ))}
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Empty state */}
        {!loading && videos.length === 0 && (
          <div className="vrv-card text-center py-20">
            <div className="text-5xl mb-4">📺</div>
            <p className="font-bold text-lg mb-2" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>No Videos Yet</p>
            <p className="text-sm" style={{ color: T.muted }}>Check back soon for spiritual content</p>
          </div>
        )}
      </div>
    </div>
  );
};

const VideoRow = ({ video, isAdmin, getUserName, getVideoUrl, getThumbnailUrl }) => (
  <a href={getVideoUrl(video.youtube_url)} target="_blank" rel="noopener noreferrer"
    className="group flex items-center gap-5 px-6 py-4 transition-colors"
    style={{ background: 'white', display: 'flex' }}
    onMouseEnter={e => e.currentTarget.style.background = '#FAFAF8'}
    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
    {/* Thumbnail */}
    <div className="relative flex-shrink-0 rounded-xl overflow-hidden" style={{ width: 144, aspectRatio: '16/9' }}>
      <img src={getThumbnailUrl(video.youtube_url)} alt={video.title || video.category} className="w-full h-full object-cover" />
      <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.25)' }}>
        <div className="w-9 h-9 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: '#FF0000' }}>
          <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
        </div>
      </div>
    </div>

    {/* Info */}
    <div className="flex-1 min-w-0">
      <p className="text-sm font-bold mb-1 truncate transition-colors group-hover:text-[#D4AF37]" style={{ fontFamily: 'Poppins, sans-serif', color: '#16213E' }}>
        {video.title || video.category}
      </p>
      {video.author && <p className="text-xs mb-1" style={{ color: '#6B7280' }}>By {video.author}</p>}
      <div className="flex items-center gap-2 flex-wrap">
        {video.createdBy && (
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #D4AF37, #B9932F)', color: '#0F172A' }}>
              {getUserName(video.createdBy).charAt(0).toUpperCase()}
            </div>
            <span className="text-xs" style={{ color: '#6B7280' }}>{getUserName(video.createdBy)}</span>
          </div>
        )}
        {video.createdAt && <span className="text-xs" style={{ color: '#6B7280' }}>· {new Date(video.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
        {isAdmin && video.visibility === 'PRIVATE' && <span className="badge badge-muted">Private</span>}
      </div>
    </div>

    {/* YouTube icon */}
    <svg className="w-5 h-5 flex-shrink-0 opacity-30 group-hover:opacity-100 transition-opacity" style={{ color: '#FF0000' }} viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  </a>
);

export default YoutubePage;
